import Link from 'next/link'
import { PATHS } from 'common/const'
import HomeLayout from 'layouts/HomeLayout'
import { PageHeader } from '@/components/PageHeader'
import {
  DocumentPlusIcon,
  ListBulletIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'
import { ButtonWithIcon } from '@/components/input'
import { getAllNotes } from '@/common/notes'
import { useUser } from '@supabase/auth-helpers-react'
import { useEffect, useState, useMemo } from 'react'
import { handleAPIRequest } from '@/common/request'
import { createNoteLinkWithID } from '@/common/utils'
import {
  CheckBadgeIcon,
  ExclamationCircleIcon,
} from '@heroicons/react/20/solid'
import { Database } from 'types/supabase'
import { PageSection } from '@/components/PageSection'
import MainContainer from '@/components/MainContainer'
import { Tooltip } from '@/components/Tooltip'

type NotesList = Database['public']['Functions']['list_notes']['Returns']

const seenHowItWorks = 'seen-how-it-works'

function HowItWorksBanner({ onDismiss }: { onDismiss: () => void }) {
  return (
    <div className="mb-4 rounded-md border border-blue-200 bg-blue-50 dark:border-gray-600 dark:bg-gray-800 p-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="font-medium text-blue-900 dark:text-white mb-2">
            How it works
          </p>
          <ol className="space-y-2 text-sm text-blue-800 dark:text-gray-300">
            <li>
              <span className="font-semibold">1.</span> Write your note and add
              the recipients who should receive it.
            </li>
            <li>
              <span className="font-semibold">2.</span> Activate your note with
              a one-time payment to start monitoring.
            </li>
            <li>
              <span className="font-semibold">3.</span> Sign in at least once
              every 30, 60, or 90 days. If you stop, your note is delivered
              automatically.
            </li>
          </ol>
        </div>
        <button
          onClick={onDismiss}
          aria-label="Dismiss"
          className="shrink-0 text-blue-400 dark:text-gray-400 hover:text-blue-600 dark:hover:text-gray-200 transition-colors"
        >
          <XMarkIcon className="w-5 h-5" />
        </button>
      </div>
    </div>
  )
}

function EmptyNotes() {
  return (
    <div className="p-8 flex flex-col justify-center items-center space-y-4 text-center">
      <ListBulletIcon className="w-10 h-10 text-gray-400 dark:text-gray-500" />
      <div>
        <p className="font-medium text-gray-700 dark:text-gray-200">
          No notes yet
        </p>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Create your first note to get started.
        </p>
      </div>
      <Link href={PATHS.NEW_NOTE} className="btn blue">
        Create a note
      </Link>
    </div>
  )
}

function NoteStatusBadge({
  note,
}: {
  note: NotesList[number]
}) {
  if (note.is_active) {
    return (
      <Tooltip
        label={`Active. Your note will be delivered if you stop checking in for ${note.send_event_days} days.`}
      >
        <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 dark:text-green-400 cursor-default">
          <CheckBadgeIcon className="w-3.5 h-3.5" />
          Active ({note.send_event_days}-day check-in)
        </span>
      </Tooltip>
    )
  }

  return (
    <Link
      href={createNoteLinkWithID(note?.id, 'settings')}
      className="inline-flex items-center gap-1 text-xs font-medium rounded-full px-2 py-0.5 bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300 hover:bg-yellow-200 dark:hover:bg-yellow-900/60 transition-colors"
    >
      Not active
    </Link>
  )
}

function RecipientsBadge({ note }: { note: NotesList[number] }) {
  const hasNoRecipients = note.receivers === 0
  const isWarning = note.is_active && hasNoRecipients

  return (
    <Link
      href={createNoteLinkWithID(note?.id, 'recipients')}
      className={`inline-flex items-center gap-1 text-xs transition-colors ${
        isWarning
          ? 'font-medium text-orange-600 dark:text-orange-400 hover:text-orange-700'
          : 'text-gray-500 dark:text-gray-400 hover:text-blue-500'
      }`}
    >
      {isWarning && <ExclamationCircleIcon className="w-3.5 h-3.5" />}
      {hasNoRecipients
        ? isWarning
          ? 'No recipients — add one'
          : 'No recipients'
        : `${note.receivers} ${note.receivers === 1 ? 'recipient' : 'recipients'}`}
    </Link>
  )
}

function ListNotes({ notes }: { notes: NotesList }) {
  const sortedNotes = useMemo(
    () => [...notes].sort((a, b) => Number(b.is_active) - Number(a.is_active)),
    [notes]
  )

  if (!sortedNotes.length) return <EmptyNotes />

  return (
    <ul role="list" className="divided-y">
      {sortedNotes.map((note) => (
        <li key={note?.id}>
          <Link
            href={createNoteLinkWithID(note?.id, '')}
            className="flex items-center px-4 py-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
          >
            <div className="min-w-0 flex-1">
              <p className="font-medium truncate text-gray-900 dark:text-gray-100 hover:text-blue-500 dark:hover:text-blue-400 transition-colors">
                {note?.note_title || 'Unnamed Note'}
              </p>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1">
                <NoteStatusBadge note={note} />
                <RecipientsBadge note={note} />
              </div>
            </div>
          </Link>
        </li>
      ))}
    </ul>
  )
}

export default function NotesIndex() {
  const user = useUser()
  const [notesList, setNotesList] = useState([])
  const [showBanner, setShowBanner] = useState(false)

  const dismissBanner = () => {
    setShowBanner(false)
    window.localStorage.setItem(seenHowItWorks, 'true')
  }

  useEffect(() => {
    if (!user?.id) return

    handleAPIRequest(getAllNotes(), {
      successMessage: null,
      successCallback: (list) => setNotesList(list),
      errorMessage: 'There was an error getting your notes. Please try again.',
    })
  }, [user])

  useEffect(() => {
    const hasSeen = window.localStorage.getItem(seenHowItWorks)
    setShowBanner(hasSeen == null)
  }, [])

  return (
    <HomeLayout title="Your notes" requireAuth={true}>
      <MainContainer width={4}>
        <div className="max-w-xl">
          <PageHeader heading="Your notes">
            <Link href={PATHS.NEW_NOTE}>
              <ButtonWithIcon
                color="blue"
                Icon={DocumentPlusIcon}
                classes="items-center inline-flex"
              >
                New note
              </ButtonWithIcon>
            </Link>
          </PageHeader>
          {showBanner && <HowItWorksBanner onDismiss={dismissBanner} />}
          <PageSection className="pb-8">
            <ListNotes notes={notesList} />
          </PageSection>
        </div>
      </MainContainer>
    </HomeLayout>
  )
}
